import axios from 'axios';
import {updateFilters} from 'ducks/filters';
import * as salesActions from 'actionCreators/comparableSales';
import * as listingsActions from 'actionCreators/comparableListings';
import * as rentalsActions from 'actionCreators/comparableRentals';
import {awaitFeatureFlagReset} from './featureFlags';
import {resetReportType} from './reportType';
import {toggleFloorPlan, updateSectionValue} from './reportSections';
import {getPropertyPreferences, updatePropertyPreferences} from 'api/propertyPreferences';
import {updateIndicativeSellingPrice} from './indicativeSellingPrice';
import {updateSellersReservePrice} from './sellersReservePrice';
import {updateFewerComparablesReason} from './fewerComparablesReason';
import {selectPropertyNotes} from './propertyNotes';
import omit from 'lodash/omit';
import {immediate} from 'api/util';
import PropertyDetailImages from 'lib/PropertyDetailImages';
import {addCustomCoverImage, mkImageSelection} from './coverPageImages';
import {PropertyDetailsApi} from 'api/PropertyDetailsApi';
import {filterExpiredPreferences} from 'lib/ResumeReportUtil';
import {isEmpty} from 'lodash/lang';
import {putCache} from 'ducks/propertyDetailCacheSlice';
import {updateReportAlert} from 'ducks/alerts';
import {fetchPropertyAvm, fetchPropertyTimeline} from 'actionCreators/propertyFetchingActions';
import {isNZLocale} from 'lib/PropertyUtils';
import {fetchNzTitles} from 'ducks/nzTitles';
import { getErrorsFromFilters } from 'components/Filters/Filters';
import RapidBatchSearchApi from 'api/RapidBatchSearchApi';
import rapidSearch from 'constants/rapidSearch';

export const RESET = 'pulp/propertyDetail/RESET';
export const LOAD_PROPERTY_DETAILS = 'pulp/propertyDetail/LOAD_PROPERTY_DETAILS';
export const UPDATE = 'pulp/propertyDetail/UPDATE';
export const UPDATE_FETCHING = 'pulp/propertyDetail/UPDATE_FETCHING';
export const UPDATE_IMAGE_URI = 'pulp/propertyDetail/UPDATE_IMAGE_URI';
export const UPDATE_SELECTED_IMAGE = 'pulp/propertyDetail/UPDATE_SELECTED_IMAGE';
export const UPDATE_TARGET_IMAGE = 'pulp/propertyDetail/UPDATE_TARGET_IMAGE';

export const initialState = {
  selectedImages: [],
  propertyPhotoList: [],
  targetPhotoList: [],
  uploadPhotoList: [],
  propertyAvm: {
    confidence: 'Low'
  },
  fetching: {},
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case UPDATE:
      return {...state, ...action.payload};
    case RESET:
      return {...initialState, ...action.payload};
    case UPDATE_IMAGE_URI:
      return {
        ...state,
        propertyPhotoList: appendImageIfNotPresent(state.propertyPhotoList, action.payload.imageUri),
        uploadPhotoList: appendImageIfNotPresent(state.uploadPhotoList, action.payload.imageUri),
      };
    case UPDATE_SELECTED_IMAGE: {
      const {image, index} = action.payload;
      let selectedImages = PropertyDetailImages.summaryPropertyPhotoList(state);
      selectedImages[index] = image;
      return {
        ...state,
        propertyPhotoList: appendImageIfNotPresent(state.propertyPhotoList, image),
        uploadPhotoList: state.propertyPhotoList.includes(image) ? state.uploadPhotoList: [...state.uploadPhotoList, image],
        selectedImages,
      };
    }
    case UPDATE_TARGET_IMAGE: {
      const {imageUrl, index} = action.payload;
      let targetPhotoList = PropertyDetailImages.targetPropertyPhotoList(state);
      targetPhotoList[index] = imageUrl;
      return {
        ...state,
        propertyPhotoList: appendImageIfNotPresent(state.propertyPhotoList, imageUrl),
        uploadPhotoList: state.propertyPhotoList.includes(imageUrl) ? state.uploadPhotoList: [...state.uploadPhotoList, imageUrl],
        targetPhotoList,
      };
    }
    case UPDATE_FETCHING: {
      const {payload: {fetching: actionFetching}} = action;
      const stateFetching = state.fetching;
      let newFetching;
      if (isEmpty(actionFetching)) {
        newFetching = {};
      } else {
        newFetching = Object.entries({...stateFetching, ...actionFetching})
          .filter(([_, value]) => value === true)
          .reduce((acc, [key, value]) => ({
            ...acc,
            [key]: value,
          }), {});
      }
      return {
        ...state,
        fetching: newFetching,
      };
    }
    default:
      return state;
  }
}

const appendImageIfNotPresent = (images, imageUrl) => {
  if (imageUrl && !images.includes(imageUrl)) {
    return [...images, imageUrl];
  }
  return images;
};

const propertyLocationAvailableCallbacks = (() => {
  let callbacks = [];
  return {
    push: (cb) => callbacks.push(cb),
    resolve: () => immediate().then(() => {
      callbacks.forEach(p => p());
      callbacks = [];
    }),
  };
})();

export function resetPropertyDetail(payload) {
  if (payload.latitude && payload.longitude) {
    propertyLocationAvailableCallbacks.resolve();
  }
  return {type: RESET, payload};
}

/**
 * @param propertyDetail
 * @returns {Promise<unknown>}
 */
async function resolveLocationBasedCallbacks(propertyDetail) {
  if (propertyDetail.latitude && propertyDetail.longitude) {
    return propertyLocationAvailableCallbacks.resolve();
  }
}

async function retrieveSuburbInsightsForPropertyType(propertyId, propertyType, coords = {}) {
  const suburbInsights = await PropertyDetailsApi.getSuburbInsights(propertyId, propertyType, coords);
  return {
    ...suburbInsights,
    isLoading: false,
  };
}

function retrieveRapidDetailsAndUpdatePropertyDetail({ propertyId }) {
  return async (dispatch) => {
    return Promise.all([
      dispatch(fetchPropertyAvm({propertyId})),
      dispatch(fetchPropertyTimeline({propertyId})),
    ].concat(isNZLocale() ? [
      dispatch(fetchNzTitles(propertyId)),
    ] : []));
  };
}

const getSuburbInsights = async (state, payload, coords = {}) => {
  const suburbInsights =
      await retrieveSuburbInsightsForPropertyType(state.propertyDetail.propertyId, payload.propertyType, coords);
  if (suburbInsights) {
    return { ...payload, suburbInsights };
  }
  return payload;
};

export function retrieveSuburbInsightsAndUpdatePropertyDetail(payload) {
  return async (dispatch, getState) => {
    if (payload.propertyType) {
      payload = await getSuburbInsights(getState(), payload);
    }
    dispatch({type: UPDATE, payload});

    if (payload?.isCustomLatLng && payload.latitude && payload.longitude) {
      const coords = {lat: payload.latitude, lng: payload.longitude};
      payload = await getSuburbInsights(getState(), payload, coords);
      dispatch({type: UPDATE, payload});
      propertyLocationAvailableCallbacks.resolve();
    }
  };
}

function handleCoverPageImagesPreference({dispatch, key, value, propertyId}) {
  const reportTypeRegex = /^coverPageImages.selection.(\w+)$/;
  let reportType;
  if ((reportType = key.match(reportTypeRegex))) {
    const selection = {
      reportType: reportType[1],
      ...(value || {}),
    };
    dispatch(mkImageSelection(selection));
  }
  else if (/^coverPageImages.custom\..+$/.test(key) && /^([^@]+)@.+$/.test(value)) {
    const valueMatch = value.match(/^([^@]+)@(.+)$/);
    const type = valueMatch[1];
    const imageUri = valueMatch[2];
    dispatch(addCustomCoverImage({
      imageUri,
      type,
    }));
  }
  else {
    console.log('legacy coverPageImages property preference ignored');
  }
}

let loadingPropertyDetail = null;

const PROPERTY_DETAILS_REPORT_ACTIONS = {
  cma: {
    'comparableSales.sortBy': (dispatch, value) => dispatch(salesActions.updateSortBy(value)),
    'comparableListings.sortBy': (dispatch, value) => dispatch(listingsActions.updateSortBy(value)),
    'comparableSales.properties': (dispatch, value) => dispatch(salesActions.updateForSession(value)),
    'comparableListings.properties': (dispatch, value) => dispatch(listingsActions.updateForSession(value)),
    'propertyNotes.sales': (dispatch, value) => {
      value.forEach((propertyId) => dispatch(selectPropertyNotes(propertyId, 'sales', true)));
    },
    'propertyNotes.listings': (dispatch, value) => {
      value.forEach((propertyId) => dispatch(selectPropertyNotes(propertyId, 'listings', true)));
    },
  },
  rental: {
    'comparableRentals.sortBy': (dispatch, value) => dispatch(rentalsActions.updateSortBy(value)),
    'comparableRentals.properties': (dispatch, value) => dispatch(rentalsActions.updateForSession(value)),
    'propertyNotes.rentals': (dispatch, value) => {
      value.forEach((propertyId) => dispatch(selectPropertyNotes(propertyId, 'rentals', true)));
    },
  },
  soi: {
    'comparableSales.sortBy': (dispatch, value) => dispatch(salesActions.updateSortBy(value)),
    'comparableSales.properties': (dispatch, value) => dispatch(salesActions.updateForSession(value)),
    'propertyNotes.sales': (dispatch, value) => {
      value.forEach((propertyId) => dispatch(selectPropertyNotes(propertyId, 'sales', true)));
    },
  },
  indicativeSellingPrice: (dispatch, value) => dispatch(updateIndicativeSellingPrice(value)),
  sellersReservePrice: (dispatch, value) => dispatch(updateSellersReservePrice(value)),
  fewerComparablesReason: (dispatch, value) => dispatch(updateFewerComparablesReason(value)),
  callback: {
    cma: (dispatch, preferences) => () => {
      const selectedSales = preferences['comparableSales.selected'] || [];
      const comparableSalesSelectedMoved = preferences['comparableSales.selectedMoved'];
      if (comparableSalesSelectedMoved) {
        dispatch(salesActions.orderSelected(selectedSales));
      }
      selectedSales.forEach(p => dispatch(salesActions.fetchItem(p, false)));
      const featuredSales = preferences['comparableSales.featured'] || [];
      featuredSales.forEach((f) => dispatch(salesActions.featureProperty(f)));

      const selectedListings = preferences['comparableListings.selected'] || [];
      const comparableListingsSelectedMoved = preferences['comparableListings.selectedMoved'];
      if (comparableListingsSelectedMoved) {
        dispatch(listingsActions.orderSelected(selectedListings));
      }
      selectedListings.forEach((p) => dispatch(listingsActions.fetchItem(p, false)));
      const featuredListings = preferences['comparableListings.featured'] || [];
      featuredListings.forEach((f) => dispatch(listingsActions.featureProperty(f)));
    },
    rental: (dispatch, preferences) => () => {
      const selectedRentals = preferences['comparableRentals.selected'] || [];
      const comparableRentalsSelectedMoved = preferences['comparableRentals.selectedMoved'];
      if (comparableRentalsSelectedMoved) {
        dispatch(rentalsActions.orderSelected(selectedRentals));
      }
      selectedRentals.forEach((p) => dispatch(rentalsActions.fetchItem(p, false)));
      const featuredRentals = preferences['comparableRentals.featured'] || [];
      featuredRentals.forEach((f) => dispatch(rentalsActions.featureProperty(f)));
    },
    soi: (dispatch, preferences) => () => {
      const selectedSales = preferences['comparableSales.selected'] || [];
      const comparableSalesSelectedMoved = preferences['comparableSales.selectedMoved'];
      if (comparableSalesSelectedMoved) {
        dispatch(salesActions.orderSelected(selectedSales));
      }
      selectedSales.forEach(p => dispatch(salesActions.fetchItem(p, false)));
      const featuredSales = preferences['comparableSales.featured'] || [];
      featuredSales.forEach((f) => dispatch(salesActions.featureProperty(f)));
    }
  }
};

/**
 * Load state into the report based on the preferences
 * @param dispatch
 * @param preferences
 * @param propertyDetail
 * @todo State structure should have separate comparable items and preference data instead of mutating the API's data
 */
function autoResumeReport(dispatch, preferences, propertyDetail, reportType) {
  if (preferences.reportType) {
    dispatch(resetReportType(preferences.reportType));
  }

  const getEvaluatedPreferenceFilters = () => {
    const errors = getErrorsFromFilters(preferences.filters);
    const hasError = Object.keys(errors).some(filterHasErrors => filterHasErrors);
    return hasError
        ? { ...preferences.filters, errors }
        : omit(preferences.filters, 'errors');
  };

  const evaluatedPreferenceFilters = getEvaluatedPreferenceFilters();
  if (preferences.filters && !evaluatedPreferenceFilters?.errors) {
    dispatch(updateFilters(evaluatedPreferenceFilters));
  } else {
    dispatch(updateFilters({propertyTypes: [propertyDetail.propertyType]}));
  }

  const startsWith = (preference) => _key => preference.startsWith(_key);
  for (let [key, value] of Object.entries(preferences)) {
    let action = PROPERTY_DETAILS_REPORT_ACTIONS[key] ||
        PROPERTY_DETAILS_REPORT_ACTIONS[reportType][key];
    if (!action) {
      const propertiesKey = Object.keys(PROPERTY_DETAILS_REPORT_ACTIONS[reportType]).find(startsWith(key));
      if (!!propertiesKey) {
        action = PROPERTY_DETAILS_REPORT_ACTIONS[reportType][propertiesKey];
      }
    }

    if (!!action) {
      action(dispatch, value);
    } else if (/^(coverPageImages\..+)$/.test(key)) {
      handleCoverPageImagesPreference({
        dispatch,
        key,
        value,
        propertyId: propertyDetail.propertyId,
      });
    } else if (/^[a-z_]+\.sectionData\./.test(key)) {
      dispatch(updateSectionValue({
        key: key.substring(0, key.indexOf('.')).toUpperCase(),
        value,
        dataKeyPath: key.substring(key.indexOf('.') + 1),
        reportType: preferences.reportType || 'cma',
      }));
    } else if (key === 'targetPhotoList') {
      propertyDetail.targetPhotoList = value;
    } else if (key === 'selectedImages') {
      propertyDetail.selectedImages = value;
    }
  }

  if (['cma', 'rental', 'soi'].includes(reportType)) {
    propertyLocationAvailableCallbacks
        .push(PROPERTY_DETAILS_REPORT_ACTIONS.callback[reportType](dispatch, preferences));
  }
}

export function loadPropertyDetail(propertyId) {
  return async (dispatch, getState) => {
    if (loadingPropertyDetail === propertyId) {
      console.log('YOU LOADED THE PROPERTY TWICE');
      return;
    }
    try {
      await Promise.all([awaitFeatureFlagReset()]);
      const {featureFlags: {enableUndisclosedPriceInUI}, settings = {}, reportType: _reportType} = getState();
      loadingPropertyDetail = propertyId;
      const propertyDetailsPromise = () => {
        return RapidBatchSearchApi.getRapidBatchSearch(
            { propertyIds: [propertyId] },
            rapidSearch.COMPARABLE_TYPE.SALES,
          ).then(res => {
            const property = res?.data?.[0];
            dispatch(putCache(property));
            return property;
          });
      };
      const [propertyDetail, preferencesData] = await Promise.all([
        propertyDetailsPromise(),
        getPropertyPreferences(propertyId),
      ]);

      const filteredPreferencesData = filterExpiredPreferences(preferencesData, settings);

      const preferences = filteredPreferencesData
        .reduce((acc, pref) => {
          try {
            acc[pref.key] = JSON.parse(pref.value);
          } catch (e) {
            //ignore broken json
          }
          return acc;
        }, {});


      // the property id is required by the fetchItem calls below.
      // sending the full propertyDetail here will cause the ComparablesPage and ComparableListingsPage
      // to do searches before the preferences are loaded.
      dispatch(resetPropertyDetail({propertyId}));
      propertyDetail.targetPhotoList = PropertyDetailImages.targetPropertyPhotoList(propertyDetail);
      propertyDetail.selectedImages = PropertyDetailImages.summaryPropertyPhotoList(propertyDetail);

      const hideLastSoldPrice = !enableUndisclosedPriceInUI && propertyDetail.isPriceWithheld;
      if (hideLastSoldPrice) {
        propertyDetail.salePrice = 0;
      }

      autoResumeReport(dispatch, preferences, propertyDetail, _reportType.value);

      dispatch({
        type: UPDATE,
        payload: {
          ...propertyDetail,
          suburbInsights: {isLoading: true}
        }
      });

      await resolveLocationBasedCallbacks(propertyDetail);
        if (propertyDetail?.latitude && propertyDetail?.longitude) {
            dispatch(retrieveSuburbInsightsAndUpdatePropertyDetail(propertyDetail));
        }

      dispatch(retrieveRapidDetailsAndUpdatePropertyDetail(propertyDetail));

      const reportType = preferences.reportType || 'cma';
      // TODO: Rapid Search
      if (reportType !== 'soi') {
        dispatch(toggleFloorPlan(reportType, propertyDetail.floorPlanUris));
      }
    } catch (e) {
      dispatch(resetPropertyDetail({error: 'Failed to load'}));
      dispatch(updateReportAlert({
        isShown: true,
        msg: 'Some details failed to load. Kindly refresh.',
      }));
    } finally {
      loadingPropertyDetail = null;
    }
  };
}

export function notifyChangedPropertyDetail(forceUseEmail = false) {
  return (dispatch, getState) => {
    const {
      featureFlags: {emailPropertyDataUpdate, useDataUpdateApi},
      propertyDetail,
      userDetails: {
        firstName,
        lastName,
        workEmail: userEmail,
        tradeName: customerName,
        username: userId,
      },
    } = getState();

    if (emailPropertyDataUpdate || useDataUpdateApi) {
      axios.put(`/api/properties/${ propertyDetail.propertyId }`, {
        ...propertyDetail,
        userName: `${ firstName } ${ lastName }`,
        userId,
        userEmail,
        customerName,
        useDataUpdateApi: !forceUseEmail && useDataUpdateApi,
      });
    }
  };
}

export function updatePropertyImageUri(imageUri) {
  return {
    type: UPDATE_IMAGE_URI,
    payload: {
      imageUri,
    },
  };
}

export function updateSelectedPropertyImage(index, image, savePreference = false) {
  return async (dispatch, getState) => {
    dispatch({
      type: UPDATE_SELECTED_IMAGE,
      payload: {index, image},
    });

    if (savePreference) {
      const {
        propertyDetail: {propertyId, selectedImages},
      } = getState();
      const newSelectedImages = [...selectedImages];
      newSelectedImages[index] = image;
      updatePropertyPreferences(propertyId, {selectedImages: newSelectedImages});
    }
  };
}

export function updateTargetPhotoList(index, imageUrl, savePreference = false) {
  return async (dispatch, getState) => {
    dispatch({
      type: UPDATE_TARGET_IMAGE,
      payload: {index, imageUrl},
    });

    if (savePreference) {
      const {
        propertyDetail: {propertyId, targetPhotoList},
      } = getState();
      let newTargetPhotoList = [...targetPhotoList];
      newTargetPhotoList[index] = imageUrl;
      updatePropertyPreferences(propertyId, {targetPhotoList: newTargetPhotoList});
    }
  };
}
